import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  integrations,
  type Integration,
  type IntegrationStatus,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';
import { classeDaAba, trilhoDeAbas } from '@/shared/ui/tabStyles';

import { IntegrationCallsTab } from './IntegrationCallsTab';

/**
 * Registro do que a plataforma integra com provedores externos.
 *
 * A regra vem de docs/INTEGRATIONS.md e e uma so: nunca apresentar simulacao como
 * integracao real. A tela mostra o estado que o servidor afirma, e o servidor so
 * afirma conexao onde existe adaptador.
 *
 * "Em uso" e o que a plataforma consulta agora (ADR 0092): o provedor publico (CEP e
 * CNPJ pela BrasilAPI) sempre, e o da plataforma (Vigidata) quando a configuracao do
 * servidor o liga. O da plataforma nao tem Configurar nem Verificar: a empresa nao
 * escolhe a situacao dele.
 */

const rotulos: Record<
  IntegrationStatus,
  { label: string; tone: 'neutral' | 'info' | 'warning' | 'success' | 'danger' }
> = {
  nao_configurada: { label: 'Nao configurada', tone: 'neutral' },
  simulada: { label: 'Simulada', tone: 'warning' },
  sandbox: { label: 'Sandbox', tone: 'info' },
  conectada: { label: 'Conectada', tone: 'success' },
  erro: { label: 'Erro', tone: 'danger' },
  desativada: { label: 'Desativada', tone: 'neutral' },
};

type Aba = 'provedores' | 'registro';

const abas: { chave: Aba; rotulo: string }[] = [
  { chave: 'provedores', rotulo: 'Provedores' },
  { chave: 'registro', rotulo: 'Registro' },
];

export function IntegrationsPage() {
  const { can } = useAuth();
  const [aba, setAba] = useState<Aba>('provedores');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Integracoes"
        description="Conexoes com fontes de dados e servicos de terceiros."
      />

      {/* Provedores e a configuracao de cada um; Registro e a "Consulta integracoes"
          do OnPoint, com as chamadas feitas e o reenvio. */}
      <div role="tablist" aria-label="Integracoes" className={trilhoDeAbas}>
        {abas.map((item) => (
          <button
            key={item.chave}
            type="button"
            role="tab"
            id={`aba-${item.chave}`}
            aria-selected={aba === item.chave}
            aria-controls={`painel-${item.chave}`}
            onClick={() => setAba(item.chave)}
            className={aba === item.chave ? classeDaAba(true) : classeDaAba(false)}
          >
            {item.rotulo}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`painel-${aba}`} aria-labelledby={`aba-${aba}`}>
        {aba === 'provedores' ? (
          <ProvidersPanel canConfigure={can('integracoes.integracao.update')} />
        ) : (
          <IntegrationCallsTab canResend={can('integracoes.integracao.update')} />
        )}
      </div>
    </div>
  );
}

function ProvidersPanel({ canConfigure: podeConfigurar }: { canConfigure: boolean }) {
  const [configurando, setConfigurando] = useState<Integration | null>(null);

  const consulta = useQuery({
    queryKey: ['integrations'],
    queryFn: ({ signal }) => integrations.list(signal),
  });

  const itens = consulta.data ?? [];
  const implementadas = itens.filter((item) => item.implemented);
  const categorias = Array.from(new Set(itens.map((item) => item.category)));

  return (
    <div className="flex flex-col gap-5">
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as integracoes">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Alert
        tone="info"
        title={`${implementadas.length} de ${itens.length} provedores tem adaptador implementado`}
      >
        Um provedor sem adaptador nao consulta nada, e por isso nao pode ser marcado
        como conectado. Cada integracao so comeca com documentacao oficial, credencial
        de teste, contrato e custo definidos. Os marcados &quot;Em uso&quot; sao os que
        a plataforma consulta agora.
      </Alert>

      {consulta.isPending ? (
        <p className="text-sm text-ink-500">Carregando as integracoes.</p>
      ) : (
        categorias.map((categoria) => (
          <section key={categoria} className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              {categoria}
            </h3>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {itens
                .filter((item) => item.category === categoria)
                .map((item) => (
                  <IntegrationCard
                    key={item.key}
                    integration={item}
                    canConfigure={podeConfigurar}
                    onConfigure={() => setConfigurando(item)}
                  />
                ))}
            </div>
          </section>
        ))
      )}

      {configurando ? (
        <ConfigureDialog
          integration={configurando}
          open
          onClose={() => setConfigurando(null)}
        />
      ) : null}
    </div>
  );
}

function IntegrationCard({
  integration,
  canConfigure,
  onConfigure,
}: {
  integration: Integration;
  canConfigure: boolean;
  onConfigure: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const verificar = useMutation({
    mutationFn: () => integrations.check(integration.key),
    onSuccess: (atualizada) => {
      if (atualizada.status === 'erro') {
        toast.error('O provedor nao respondeu.');
      } else {
        toast.success('Provedor verificado.');
      }
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const situacao = rotulos[integration.status];
  const daPlataforma = integration.managed_by_platform;

  return (
    <Card>
      <CardHeader
        title={integration.name}
        action={
          <span className="flex flex-wrap items-center justify-end gap-1">
            {integration.in_use ? <Badge tone="info">Em uso</Badge> : null}
            <Badge tone={situacao.tone} dot>
              {situacao.label}
            </Badge>
          </span>
        }
      />
      <CardBody className="flex flex-col gap-3">
        <p className="text-sm text-ink-600">{integration.description}</p>

        {integration.usage_note ? (
          <p className="rounded-md border border-line bg-surface-sunken px-3 py-2 text-2xs text-ink-700">
            {integration.usage_note}
          </p>
        ) : null}

        {!integration.implemented ? (
          <p className="rounded-md border border-dashed border-line-strong bg-ink-50/50 px-3 py-2 text-2xs text-ink-600">
            Sem adaptador implementado. Nada e consultado neste provedor, e ele nao pode
            ser marcado como conectado.
          </p>
        ) : null}

        {integration.last_error ? (
          <p className="text-2xs text-danger-700">{integration.last_error}</p>
        ) : null}

        {!daPlataforma ? (
          <p className="text-2xs text-ink-500">
            {integration.last_checked_at
              ? `Verificado em ${formatDateTime(integration.last_checked_at)}`
              : 'Nunca verificado'}
            {integration.changed_label ? ` · ${integration.changed_label}` : ''}
          </p>
        ) : null}

        {integration.notes ? (
          <p className="text-2xs text-ink-600">{integration.notes}</p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          {canConfigure && !daPlataforma ? (
            <Button variant="secondary" onClick={onConfigure}>
              Configurar
            </Button>
          ) : null}
          {canConfigure && integration.implemented && !daPlataforma ? (
            <Button loading={verificar.isPending} onClick={() => verificar.mutate()}>
              Verificar
            </Button>
          ) : null}
          {integration.docs_url ? (
            <a
              href={integration.docs_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-2xs text-brand-600 underline-offset-2 hover:underline"
            >
              Documentacao
            </a>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

function ConfigureDialog({
  integration,
  open,
  onClose,
}: {
  integration: Integration;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  // A situacao atual pode estar fora das que se escolhem: Erro vem da verificacao, e
  // nunca da escolha. Abrir o dialogo com ela deixava o campo com valor fora das
  // opcoes, e salvar sem mexer era recusado (47, anexo C, integracoes 4).
  const atualEscolhivel = integration.allowed_statuses.includes(integration.status);
  const sugerida: IntegrationStatus = atualEscolhivel
    ? integration.status
    : integration.allowed_statuses.includes('conectada')
      ? 'conectada'
      : (integration.allowed_statuses[0] ?? 'nao_configurada');
  const [status, setStatus] = useState<IntegrationStatus>(sugerida);
  const [notas, setNotas] = useState(integration.notes);

  const salvar = useMutation({
    mutationFn: () => integrations.configure(integration.key, status, notas),
    onSuccess: () => {
      toast.success('Integracao atualizada.');
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Configurar ${integration.name}`}
      description="Os estados oferecidos sao os que este provedor aceita. O servidor recusa qualquer outro."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button loading={salvar.isPending} onClick={() => salvar.mutate()}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!integration.implemented ? (
          <Alert tone="info" title="Sem adaptador implementado">
            Enquanto nao houver codigo falando com este provedor, ele so pode ficar nao
            configurado ou desativado. Marca-lo como conectado afirmaria uma consulta
            que nunca acontece.
          </Alert>
        ) : null}

        {!atualEscolhivel ? (
          <Alert
            tone="warning"
            title={`Situacao atual: ${rotulos[integration.status].label}`}
          >
            Esta situacao vem da verificacao contra o provedor e nao pode ser escolhida.
            Escolha uma das opcoes abaixo; para voltar a conferir, use Verificar.
          </Alert>
        ) : null}

        <Select
          label="Situacao"
          value={status}
          options={integration.allowed_statuses.map((situacao) => ({
            value: situacao,
            label: rotulos[situacao].label,
          }))}
          onChange={(event) => setStatus(event.target.value as IntegrationStatus)}
        />

        <Textarea
          label="Observacoes"
          value={notas}
          hint="Contrato, responsavel, o que falta para ligar."
          onChange={(event) => setNotas(event.target.value)}
        />
      </div>
    </Modal>
  );
}
